import { Article } from '../../components/Layout'
import { H1, H2, P, Lead, UL, LI, Callout, Code, PropTable, Steps, Step, Screenshot } from '../../components/DocComponents'

const toc = [
  { id: 'what', title: 'What are Input Params?', level: 2 as const },
  { id: 'fixed', title: 'Fixed vs dynamic values', level: 2 as const },
  { id: 'pagination', title: 'Pagination parameter', level: 2 as const },
  { id: 'url-forwarding', title: 'URL forwarding', level: 2 as const },
  { id: 'fields', title: 'Parameter fields', level: 2 as const },
]

export function InputParams() {
  return (
    <Article toc={toc}>
      <H1>Input Parameters</H1>
      <Lead>Input Parameters are named values injected into all Views when a Plate runs. They are how you pass context — like "which customer are we looking at?" — into the plate.</Lead>

      <H2 id="what">What are Input Params?</H2>
      <P>Every View in a Plate can reference one Input Parameter. For Coffee API views, the param value is used as the ID (Cup UUID, Coffee UUID, or parent Coffee UUID depending on the mode). For Drinker views, all params are passed as the <Code>context</Code> object.</P>
      <Callout type="info">Input params are defined in the <strong>Parameters tab</strong> of the Plate detail page. Each view then selects which param it uses from a dropdown in the view wizard.</Callout>

      <H2 id="fixed">Fixed vs dynamic values</H2>
      <P>A parameter can have a <strong>fixed value</strong> — a value baked in at configuration time. For example, the Cup ID for a "Products" plate is always the same; there's no point asking the user for it every time. Set a fixed value to lock it in.</P>
      <P>Parameters without a fixed value are supplied at runtime in one of two ways:</P>
      <UL>
        <LI><strong>URL query params</strong> — When the plate is opened with <Code>?paramName=value</Code> in the URL, the value is automatically picked up</LI>
        <LI><strong>Output mappings</strong> — A view can write a value from its result back into a param, which then refreshes dependent views (see <a href="/docs/plates/output-mappings" className="text-blue-600 underline underline-offset-2">Output Mappings</a>)</LI>
      </UL>

      <H2 id="pagination">Pagination parameter</H2>
      <P>To enable pagination on a <Code>cup_list</Code> view, you need a dedicated page parameter. Create a param of type <Code>number</Code>, name it e.g. <Code>page</Code>, with a default fixed value of <Code>1</Code>. Then in the view wizard, open the <strong>Pagination</strong> section and:</P>
      <UL>
        <LI>Set <Code>pageParamName</Code> to your page param name (<Code>page</Code>)</LI>
        <LI>Set <Code>totalPagesPath</Code> to <Code>totalPages</Code> (the key in the Coffee API response)</LI>
        <LI>Set <Code>currentPagePath</Code> to <Code>page</Code></LI>
      </UL>
      <P>The Plate Runner automatically renders a pagination bar below the view. Clicking a page number updates the page param, which re-fetches the view.</P>
      <Screenshot alt="A table view with a pagination bar showing pages 1, 2, 3 and next/prev buttons" caption="The pagination bar is rendered automatically when a pagination config is set." />

      <H2 id="url-forwarding">URL forwarding between plates</H2>
      <P>When a user clicks a Section Button or navigates from one plate to another, all current <Code>runInputs</Code> are forwarded as URL query params. This is how context flows between plates — for example:</P>
      <UL>
        <LI>Plate A: "Preorder Overview" — has a <Code>preorderId</Code> param</LI>
        <LI>User clicks "Add Article" button</LI>
        <LI>Browser navigates to <Code>/open/:plateB?preorderId=abc-123</Code></LI>
        <LI>Plate B: "Add Article" — automatically receives <Code>preorderId=abc-123</Code> as its input param</LI>
        <LI>The Preparation Interviewer uses <Code>preorderId</Code> from the payload to link the new record to the right preorder</LI>
      </UL>
      <Callout type="tip">This URL forwarding pattern is the key to building multi-plate workflows. Design your param names consistently across plates so values flow naturally.</Callout>

      <H2 id="fields">Parameter fields</H2>
      <PropTable props={[
        { name: 'name', type: 'string', required: true, description: 'Param name as used in context, URL query params, and output mappings.' },
        { name: 'paramType', type: 'enum', required: true, description: 'string | number | date | uuid' },
        { name: 'required', type: 'boolean', default: 'false', description: 'If true and no value is provided at runtime, the view shows no data.' },
        { name: 'fixedValue', type: 'string', description: 'A constant value for this param. When set, the param is never shown to the user.' },
        { name: 'description', type: 'string', description: 'Internal note explaining what this param is used for.' },
      ]} />
    </Article>
  )
}
